// RevenueLeakSection.jsx
import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  DollarSign,
  IndianRupee,
  ShieldCheck,
  TrendingUp,
  AlertCircle,
  Clock,
} from "lucide-react";

const RevenueLeakSection = () => {
  const containerRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  // Background shifts from chaotic/messy to pure, solid white
  const bgColor = useTransform(
    scrollYProgress,
    [0.3, 0.6],
    ["#fef2f2", "#ffffff"],
  );

  // TEXT ANIMATIONS
  const text1Opacity = useTransform(
    scrollYProgress,
    [0.05, 0.15, 0.3],
    [0, 1, 0],
  );
  const text1Y = useTransform(scrollYProgress, [0.05, 0.15, 0.3], [50, 0, -50]);

  const text2Opacity = useTransform(
    scrollYProgress,
    [0.35, 0.45, 0.6],
    [0, 1, 0],
  );
  const text2Y = useTransform(scrollYProgress, [0.35, 0.45, 0.6], [50, 0, -50]);

  const text3Opacity = useTransform(scrollYProgress, [0.65, 0.75], [0, 1]);
  const text3Y = useTransform(scrollYProgress, [0.65, 0.75], [50, 0]);

  // PIPELINE ANIMATIONS
  const leakOpacity = useTransform(scrollYProgress, [0.4, 0.45], [1, 0]);
  const clampTopY = useTransform(scrollYProgress, [0.3, 0.5], ["-150%", "0%"]);
  const clampBottomY = useTransform(
    scrollYProgress,
    [0.3, 0.5],
    ["150%", "0%"],
  );
  const clampOpacity = useTransform(scrollYProgress, [0.3, 0.4], [0, 1]);

  const impactPulseScale = useTransform(
    scrollYProgress,
    [0.48, 0.5, 0.55],
    [0.5, 2.5, 3],
  );
  const impactPulseOpacity = useTransform(
    scrollYProgress,
    [0.48, 0.5, 0.55],
    [0, 0.8, 0],
  );

  const outputWidth = useTransform(
    scrollYProgress,
    [0.5, 0.65],
    ["15%", "100%"],
  );
  const outputColor = useTransform(
    scrollYProgress,
    [0.5, 0.6],
    ["#fca5a5", "#10b981"],
  );
  const outputGlow = useTransform(
    scrollYProgress,
    [0.5, 0.65],
    ["0px 0px 0px transparent", "0px 0px 30px rgba(16,185,129,0.8)"],
  );

  const sceneScale = useTransform(
    scrollYProgress,
    [0, 0.5, 1],
    [0.95, 1.05, 1],
  );

  // Sub-components
  const LeakParticle = ({ delay, xOffset, icon: Icon, color }) => (
    <motion.div
      initial={{ y: 0, opacity: 0, scale: 0 }}
      animate={{
        y: [0, 150, 300],
        opacity: [0, 1, 0],
        scale: [0.5, 1.2, 0.5],
        x: [0, xOffset, xOffset * 1.5],
        rotate: [0, xOffset > 0 ? 90 : -90, xOffset > 0 ? 180 : -180],
      }}
      transition={{
        duration: 2.5,
        delay,
        repeat: Infinity,
        ease: "easeIn",
      }}
      className={`leak-particle ${color}`}
    >
      <Icon className="leak-particle-icon" strokeWidth={2.5} />
    </motion.div>
  );

  const FloatingLabel = ({ delay, text, xOffset }) => (
    <motion.div
      initial={{ y: 0, opacity: 0 }}
      animate={{
        y: [0, 100],
        opacity: [0, 1, 0],
        x: [0, xOffset],
      }}
      transition={{ duration: 3, delay, repeat: Infinity, ease: "easeOut" }}
      className="floating-label"
    >
      <span className="floating-label-text">{text}</span>
    </motion.div>
  );

  return (
    <section ref={containerRef} className="revenue-leak-section">
      <motion.div
        style={{ backgroundColor: bgColor }}
        className="sticky-container"
      >
        {/* Abstract Grid Background */}
        <div className="grid-background" />

        {/* TEXT LAYERS */}
        <div className="text-layers">
          {/* Phase 1 - The Bleed */}
          <motion.div
            style={{ opacity: text1Opacity, y: text1Y }}
            className="text-phase"
          >
            <div className="phase-badge phase-badge-red">
              <AlertCircle className="badge-icon" />
              The Status Quo
            </div>
            <h2 className="phase-title">
              You are <span className="text-red-500">bleeding</span> revenue.
            </h2>
            <p className="phase-description">
              Unbilled hours, delayed approvals, and forgotten invoices. The
              gaps in your workflow are draining your bottom line.
            </p>
          </motion.div>

          {/* Phase 2 - The Fix */}
          <motion.div
            style={{ opacity: text2Opacity, y: text2Y }}
            className="text-phase"
          >
            <div className="phase-badge phase-badge-blue">
              <ShieldCheck className="badge-icon" />
              The Fix
            </div>
            <h2 className="phase-title">Put a hard stop to the chaos.</h2>
          </motion.div>

          {/* Phase 3 - The Result */}
          <motion.div
            style={{ opacity: text3Opacity, y: text3Y }}
            className="text-phase"
          >
            <div className="phase-badge phase-badge-green">
              <TrendingUp className="badge-icon" />
              The Result
            </div>
            <h2 className="phase-title">
              Capture <span className="gradient-text">every cent.</span>
            </h2>
            <p className="phase-description">
              100% visibility. Zero leakage. Total control over your billable
              pipeline.
            </p>
          </motion.div>
        </div>

        {/* THE PIPELINE VISUAL */}
        <motion.div
          style={{ scale: sceneScale }}
          className="pipeline-container"
        >
          {/* LEFT PIPE (Input) */}
          <div className="pipe-left">
            <div className="pipe-inner-left">
              <motion.div
                animate={{ x: ["-100%", "200%"] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
                className="pipe-flow"
              />
            </div>
          </div>

          {/* CENTER GAP (The Leak Zone) */}
          <div className="leak-zone">
            {/* Active Leaks */}
            <motion.div
              style={{ opacity: leakOpacity }}
              className="leaks-container"
            >
              <LeakParticle
                delay={0}
                xOffset={-40}
                icon={IndianRupee}
                color="text-red-500"
              />
              <LeakParticle
                delay={0.5}
                xOffset={50}
                icon={Clock}
                color="text-orange-400"
              />
              <LeakParticle
                delay={1.2}
                xOffset={-20}
                icon={DollarSign}
                color="text-red-400"
              />
              <LeakParticle
                delay={1.8}
                xOffset={30}
                icon={AlertCircle}
                color="text-orange-500"
              />
              <LeakParticle
                delay={2.2}
                xOffset={10}
                icon={IndianRupee}
                color="text-red-500"
              />

              <FloatingLabel delay={0.2} text="Unbilled Time" xOffset={-80} />
              <FloatingLabel
                delay={1.5}
                text="Delayed Approvals"
                xOffset={90}
              />
              <FloatingLabel delay={2.1} text="Lost Scope" xOffset={-40} />
            </motion.div>

            {/* The Simplifyte Seal / Clamp */}
            {/* Top Half */}
            <motion.div
              style={{ y: clampTopY, opacity: clampOpacity }}
              className="clamp-top"
            >
              <div className="clamp-mechanical-dot" />
              <span className="clamp-brand">SIMPLIFYTE</span>
              <div className="clamp-glow-bar" />
            </motion.div>

            {/* Bottom Half */}
            <motion.div
              style={{ y: clampBottomY, opacity: clampOpacity }}
              className="clamp-bottom"
            >
              <div className="clamp-glow-bar mb-1" />
              <ShieldCheck className="clamp-shield" />
              <div className="clamp-mechanical-dot-bottom" />
            </motion.div>

            {/* Lock Impact Pulse */}
            <motion.div
              style={{ scale: impactPulseScale, opacity: impactPulseOpacity }}
              className="impact-pulse"
            />
          </div>

          {/* RIGHT PIPE (Output) */}
          <div className="pipe-right">
            <motion.div
              style={{
                width: outputWidth,
                backgroundColor: outputColor,
                boxShadow: outputGlow,
              }}
              className="pipe-output"
            >
              <motion.div
                animate={{ x: ["-100%", "200%"] }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="output-flow"
              />
            </motion.div>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default RevenueLeakSection;
